<template>
    <div v-if="$store.getters.authCheck" class="default d-flex flex-column flex-grow-1">
        <navbar></navbar>
        <portal-target
            name="sticky-header"
            multiple
            :class="['sticky-header sticky-top bg-white', {'shadow': scrolledSticky}]"
            ref="stickyHeader"
        >
        </portal-target>
        <div class="body-wrapper flex-grow-1 d-flex flex-column">
            <keep-alive :include="cachedComponents">
                <router-view></router-view>
            </keep-alive>
        </div>
        <terms-conditions-modal
            v-if="!termsAccepted"
            :show.sync="modals.terms.visible"
        >
        </terms-conditions-modal>
        <client-switch-prompt-modal
            v-if="targetClient"
            :show.sync="modals.clientSwitch.visible"
            :current-client-name="currentClientName"
            :target-client-name="targetClient.client"
            :loading="clientSwitchLoading"
            @confirm="confirmClientSwitch"
            @cancel="cancelClientSwitch"
        />
    </div>
</template>

<script>
import Navbar from '@/components/Nav/Navbar';
import TermsConditionsModal from '@/components/Modal/TermsConditionsModal';
import ClientSwitchPromptModal from '@/components/Modal/ClientSwitchPromptModal';
import ScrollableWidget from '@/components/Widgets/ScrollableWidget';
import User from '@/services/User/User';
import { mapGetters, mapActions } from 'vuex';
import { pendingClientSwitch, clearPendingClientSwitch } from '@/router';

export default {
  components: {
    Navbar,
    TermsConditionsModal,
    ClientSwitchPromptModal,
    ScrollableWidget,
  },
  data() {
    return {
      cachedComponents: [
        'Programme',
      ],
      modals: {
        terms: {
          visible: false,
        },
        clientSwitch: {
          visible: false,
        },
      },
      scrolledSticky: false,
      clientSwitchLoading: false,
      pendingClientSwitch: null,
    };
  },
  computed: {
    ...mapGetters(['clients', 'userClientName']),
    termsAccepted() {
      return this.$store.getters.userAcceptedTerms;
    },
    currentClientName() {
      return this.userClientName || '';
    },
    targetClient() {
      if (!this.pendingClientSwitch) return null;
      return this.clients.find(c => c.clientId === this.pendingClientSwitch.targetClientId);
    },
  },
  watch: {
    termsAccepted: {
      handler(newTermsAccepted) {
        if (this.$store.getters.authCheck) {
          this.modals.terms.visible = !newTermsAccepted;
        }
      },
      immediate: true,
    },
    targetClient: {
      handler(newTargetClient) {
        if (newTargetClient) {
          this.modals.clientSwitch.visible = true;
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.loadPendingClientSwitch();

    const stickyHeader = this.$refs.stickyHeader.$el;
    const observer = new ResizeObserver(() => {
      document.documentElement.style.setProperty('--sticky-header-height', `${stickyHeader.offsetHeight}px`);
    });
    observer.observe(stickyHeader);
    this.$once('hook:destroyed', () => observer.disconnect());

    const handleScroll = () => {
      if (window.scrollY > 0 && window.scrollY >= this.$refs.stickyHeader.$el.offsetTop) {
        this.scrolledSticky = true;
      } else {
        this.scrolledSticky = false;
      }
    };

    window.addEventListener('scroll', handleScroll);
    this.$once('hook:destroyed', () => {
      window.removeEventListener('scroll', handleScroll);
    });

    handleScroll();
  },
  methods: {
    ...mapActions(['resetTags', 'setPropertyTenure']),

    loadPendingClientSwitch() {
      this.pendingClientSwitch = pendingClientSwitch;
    },

    clearPendingSwitch() {
      this.pendingClientSwitch = null;
      clearPendingClientSwitch();
    },

    buildUrlFromRoute(route) {
      const queryString = route.query && Object.keys(route.query).length
        ? '?' + new URLSearchParams(route.query).toString()
        : '';
      return route.path + queryString;
    },

    async confirmClientSwitch() {
      if (!this.targetClient) return;

      this.clientSwitchLoading = true;
      try {
        await User.changeClient(this.targetClient.clientId);
        await this.resetTags();
        this.setPropertyTenure(null);

        const targetRoute = this.pendingClientSwitch.targetRoute;
        this.clearPendingSwitch();

        const destinationUrl = this.buildUrlFromRoute(targetRoute);
        window.location.href = destinationUrl;
      } catch (e) {
        this.$notify({
          title: 'Error',
          text: 'Failed to switch client',
          type: 'error',
        });
        this.clientSwitchLoading = false;
      }
    },

    cancelClientSwitch() {
      this.clearPendingSwitch();
      this.modals.clientSwitch.visible = false;
    },
  },
};
</script>

